import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { MapPin, ArrowLeftRight, Undo2 } from "lucide-react";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem,
    DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const dist = (a, b) => {
    if (!a || !b) return null;
    const r = Math.PI / 180;
    const h = Math.sin(((b.lat - a.lat) * r) / 2) ** 2 +
        Math.cos(a.lat * r) * Math.cos(b.lat * r) * Math.sin(((b.lon - a.lon) * r) / 2) ** 2;
    return 6371 * 2 * Math.asin(Math.sqrt(h));
};

/** Jul-2026 (User-Wunsch „Vertretung"): Standort-Wechsel für Mitarbeiter im
 *  Header — inkl. Rückwechsel-Erinnerung, Vertretungs-Hinweis und
 *  Standort-Erkennung per Browser-Position. */
export const EmployeeSiteSwitcher = () => {
    const [state, setState] = useState(null);
    const [busy, setBusy] = useState(false);
    const hintedRef = useRef(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/me/work-site");
            setState(data?.linked ? data : null);
        } catch { setState(null); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const switchTo = async (dealerId) => {
        setBusy(true);
        try {
            await api.post("/me/work-site", { dealer_id: dealerId });
            toast.success(dealerId ? "Standort gewechselt — Rechnungen, Lager & Branding laufen jetzt über diese Filiale." : "Zurück an der Stammfiliale.");
            window.location.reload();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Standort-Wechsel fehlgeschlagen");
            setBusy(false);
        }
    };

    // Vertretungs-Hinweis: heute eingeplante Vertretung an anderem Standort
    useEffect(() => {
        if (!state || hintedRef.current) return;
        const a = state.assignment_today;
        if (a && a.site_dealer_id !== state.current_dealer_id && !sessionStorage.getItem("tw_vplan_hint")) {
            hintedRef.current = true;
            sessionStorage.setItem("tw_vplan_hint", "1");
            toast(`Vertretung eingeplant: ${a.site_label}. Jetzt Standort wechseln?`, {
                duration: 15000,
                action: { label: "Wechseln", onClick: () => switchTo(a.site_dealer_id) },
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state]);

    // Standort-Erkennung (Jul-2026): Browser-Position mit Filial-Koordinaten
    // vergleichen — Hinweis, wenn der Kollege offenbar woanders steht.
    useEffect(() => {
        if (!state || sessionStorage.getItem("tw_geo_site_hint")) return;
        const withGeo = (state.branches || []).filter((b) => b.geo);
        if (withGeo.length < 2 || !navigator.geolocation) return;
        navigator.geolocation.getCurrentPosition((pos) => {
            const me = { lat: pos.coords.latitude, lon: pos.coords.longitude };
            let nearest = null, nd = Infinity;
            withGeo.forEach((b) => { const d = dist(me, b.geo); if (d != null && d < nd) { nd = d; nearest = b; } });
            const cur = withGeo.find((b) => b.id === state.current_dealer_id);
            const curD = cur ? dist(me, cur.geo) : null;
            if (nearest && nearest.id !== state.current_dealer_id && nd < 1.5 && (curD == null || curD > 3)) {
                sessionStorage.setItem("tw_geo_site_hint", "1");
                toast(`Du bist offenbar an ${nearest.name}${nearest.site_code ? ` (${nearest.site_code})` : ""} — Standort wechseln?`, {
                    duration: 15000,
                    action: { label: "Wechseln", onClick: () => switchTo(nearest.id) },
                });
            }
        }, () => {}, { maximumAge: 300000, timeout: 8000 });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state]);

    if (!state || (state.branches || []).length < 2) return null;
    const current = state.branches.find((b) => b.id === state.current_dealer_id);
    const label = current ? (current.site_code || current.city || current.name) : "Standort";

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button
                    disabled={busy}
                    className={`inline-flex items-center gap-1.5 h-9 px-2.5 rounded-sm text-[11px] font-bold uppercase tracking-wider transition-colors disabled:opacity-50 ${
                        state.override_active
                            ? "bg-orange-500 hover:bg-orange-600 text-white ring-2 ring-orange-300"
                            : "bg-white/40 hover:bg-white/60 text-[#111827]"
                    }`}
                    title={state.override_active ? "Vertretung aktiv — du arbeitest an einer anderen Filiale" : "Arbeits-Standort wechseln (Vertretung)"}
                    data-testid="employee-site-switcher"
                >
                    <MapPin className="w-3.5 h-3.5" />
                    {label}
                    {state.override_active && <ArrowLeftRight className="w-3 h-3" />}
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72">
                <DropdownMenuLabel className="text-xs">
                    Arbeits-Standort (Vertretung)
                    {state.override_active && (
                        <span className="block text-[10px] font-normal text-orange-600 mt-0.5">
                            Vertretung aktiv — nicht vergessen zurückzuwechseln!
                        </span>
                    )}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {state.branches.map((b) => (
                    <DropdownMenuItem key={b.id} disabled={busy || b.id === state.current_dealer_id}
                        onClick={() => switchTo(b.id === state.home_dealer_id ? null : b.id)}
                        data-testid={`site-switch-${b.site_code || b.id}`}
                        className={b.id === state.current_dealer_id ? "bg-yellow-50 font-bold" : ""}>
                        <MapPin className="w-3.5 h-3.5 mr-1 shrink-0" />
                        <span className="truncate">{b.name}{b.site_code ? ` · ${b.site_code}` : ""}{b.city ? ` · ${b.city}` : ""}</span>
                        {b.id === state.home_dealer_id && <span className="ml-auto text-[9px] uppercase text-gray-400 shrink-0">Stamm</span>}
                    </DropdownMenuItem>
                ))}
                {state.override_active && (
                    <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => switchTo(null)} disabled={busy}
                            className="text-orange-700 font-bold" data-testid="site-switch-back">
                            <Undo2 className="w-3.5 h-3.5 mr-1" /> Zurück zur Stammfiliale
                        </DropdownMenuItem>
                    </>
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

export default EmployeeSiteSwitcher;
